import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { auth, db, functions } from '../helpers/firebase'
import {
  createUserWithEmailAndPassword,
  deleteUser,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged
} from 'firebase/auth'
import { doc, getDoc, getDocFromServer, updateDoc, serverTimestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import {
  LAST_ACTIVE_THROTTLE_MS,
  clearLastActiveWriteMs,
  lastActiveUids,
  markLastActiveWriteMs,
  readLastActiveWriteMs,
  canStampLastActiveAt,
  shouldWriteLastActiveAt
} from '../helpers/lastActiveAt'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

// Callable errors arrive as `functions/<code>` with a message the server wrote
// for the person reading it. The Student View and PIN screens branch on the
// code — a lockout reads differently from a wrong PIN — so the code is carried
// across instead of being flattened into a bare Error like the older wrappers.
const callableError = (error, fallback) => {
  const rethrown = new Error(error?.message || fallback)
  rethrown.code = typeof error?.code === 'string'
    ? error.code.replace('functions/', '')
    : ''
  return rethrown
}

// A signup read can cache "no document" before the profile write lands.
// Later refreshes must see the server, and fall back only when it is unreachable.
const readUserDocument = async (uid, fromServer) => {
  const reference = doc(db, 'users', uid)
  if (!fromServer) return getDoc(reference)
  try {
    return await getDocFromServer(reference)
  } catch (error) {
    return getDoc(reference)
  }
}

const trimmedName = (value) => (
  typeof value === 'string' ? value.trim().slice(0, 100) : ''
)

// A read that started before completeParentName or completeStudentUsername
// can still resolve with the old flags. Those saves already succeeded, so
// the closed modal must stay closed.
const keepClosedModal = (current, next) => {
  if (!current || !next) return next
  let profile = next
  if (current.pendingParentName === false && next.pendingParentName === true) {
    profile = {
      ...profile,
      pendingParentName: false,
      firstName: current.firstName || profile.firstName,
      lastName: current.lastName || profile.lastName,
      pendingOnboardingChoice: true
    }
  }
  if (current.pendingStudentUsername === false && next.pendingStudentUsername === true) {
    profile = {
      ...profile,
      pendingStudentUsername: false,
      username: current.username || profile.username,
      pendingStudentTutorial: current.pendingStudentTutorial === true
    }
  }
  return profile
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState()
  const [userProfile, setUserProfile] = useState()
  // Uid whose profile document is actually loaded. Signup sets currentUser
  // before provisionStudentProfile creates users/{uid}, and lastActiveAt
  // cannot update a document that is not there yet.
  const [loadedProfileUid, setLoadedProfileUid] = useState(null)
  const [loading, setLoading] = useState(true)
  const profileLoadGen = useRef(0)
  // Bumped when a callable has already applied its result locally. A profile
  // read that started before that write must not put the modal flags back.
  const profileRevision = useRef(0)
  const userProfileRef = useRef()
  userProfileRef.current = userProfile
  // True while the parent dashboard is running Student View (Phase 4). App-level
  // chrome reads this so parent-facing surfaces — What's New today, anything
  // added later — stay out of a child's screen while the device is handed over.
  const [studentViewActive, setStudentViewActive] = useState(false)
  // The Student View child's document id, mirrored from ParentDashboard so
  // lastActiveAt can be stamped on credential-light students who have no
  // Auth user of their own. Cleared when Student View ends or unmounts.
  const [studentViewActingStudentId, setStudentViewActingStudentId] = useState(null)

  // True while the parent dashboard has the demo, the Add Student modal, or
  // an expanded Getting Started checklist panel open (Phase 3/5). These are
  // the destinations the post-name choice modal leads to (and their later
  // reopen paths via checklist / Settings / side menu), plus the checklist
  // panel that auto-opens immediately after the legacy tutorial finishes.
  // App-level chrome (What's New) reads this so a release-notes popup never
  // barges in front of a demo the parent is running with their child watching,
  // a half-filled Add Student form, or the checklist panel itself.
  const [parentTaskModalActive, setParentTaskModalActive] = useState(false)

  // Deletes the Auth user created for a signup whose profile write failed, then
  // signs out, so the email is not left without a profile. deleteUser requires
  // the session that just created the user, so it runs before sign-out.
  async function discardUnprovisionedAuthUser(user) {
    try {
      await deleteUser(user)
    } catch (deleteError) {
      console.error('Failed to delete unprovisioned Auth user:', deleteError)
    }
    try {
      await signOut(auth)
    } catch (signOutError) {
      console.error('Failed to sign out after signup rollback:', signOutError)
    }
  }

  // Public signup creates parent accounts. Self-created students use
  // signupStudent. A parent-created student still goes through
  // createStudentAccount.
  //
  // The Firestore profile is written by provisionParentProfile rather than here:
  // clients are not permitted to write identity or consent fields.
  //
  // The checkboxes are client-controlled UI state. provisionParentProfile only
  // trusts them to gate whether a profile is created, and writes the actual
  // consent timestamp and version server-side.
  async function signup(email, password, consent) {
    const result = await createUserWithEmailAndPassword(auth, email, password)
    try {
      const provisionProfile = httpsCallable(functions, 'provisionParentProfile')
      await provisionProfile({
        guardianConsent: consent.guardianConsent,
        termsAccepted: consent.termsAccepted
      })
    } catch (error) {
      await discardUnprovisionedAuthUser(result.user)
      throw error
    }

    await sendEmailVerification(result.user)
    return result
  }

  // Student self-signup. The Auth user is created here, then
  // provisionStudentProfile writes the age attestation and legal consent.
  // A failed profile write deletes that Auth user before the error is shown.
  async function signupStudent(email, password, consent) {
    const result = await createUserWithEmailAndPassword(auth, email, password)
    try {
      const provisionProfile = httpsCallable(functions, 'provisionStudentProfile')
      await provisionProfile({
        ageAttested: consent.ageAttested === true,
        termsAccepted: consent.termsAccepted === true,
        password
      })
    } catch (error) {
      await discardUnprovisionedAuthUser(result.user)
      throw error
    }

    await sendEmailVerification(result.user)
    return result
  }

  // Saves the blocking username for a self-created student. The server
  // rejects an email, a phone number, and a taken name. The modal stays up
  // until this succeeds.
  async function completeStudentUsername(username) {
    try {
      const complete = httpsCallable(functions, 'completeStudentUsername')
      const result = await complete({ username })
      const saved = result && result.data && typeof result.data.username === 'string'
        ? result.data.username
        : (typeof username === 'string' ? username.trim() : '')
      profileRevision.current += 1
      setUserProfile((current) => {
        if (!current) return current
        const wasPending = current.pendingStudentUsername === true
        return {
          ...current,
          username: saved,
          pendingStudentUsername: false,
          pendingStudentTutorial: wasPending ? true : current.pendingStudentTutorial === true
        }
      })
      return result.data
    } catch (error) {
      const errorMessage = error.message || 'Could not save that username'
      throw new Error(errorMessage)
    }
  }

  // Submits the blocking post-signup name modal (Phase 1). Server validates
  // and trims both names and clears pendingParentName so the modal never
  // shows again for this account.
  async function completeParentName(firstName, lastName) {
    try {
      const complete = httpsCallable(functions, 'completeParentName')
      const savedFirst = trimmedName(firstName)
      const savedLast = trimmedName(lastName)
      const result = await complete({ firstName: savedFirst, lastName: savedLast })
      profileRevision.current += 1
      setUserProfile((current) => {
        if (!current) return current
        return {
          ...current,
          firstName: savedFirst,
          lastName: savedLast,
          pendingParentName: false,
          pendingOnboardingChoice: true
        }
      })
      return result.data
    } catch (error) {
      const errorMessage = error.message || 'Error saving your name'
      throw new Error(errorMessage)
    }
  }

  // Resolves the post-name onboarding choice modal (Phase 3): Add a student,
  // See a demo, or dismiss (X) all resolve it exactly once. The server clears
  // pendingOnboardingChoice so the modal never reopens for this account, and
  // records onboardingChoiceDismissedAt only when the parent explicitly
  // dismissed rather than picking a path.
  async function completeOnboardingChoice(dismissed = false) {
    try {
      const resolveChoice = httpsCallable(functions, 'resolveOnboardingChoice')
      const result = await resolveChoice({ dismissed: !!dismissed })
      return result.data
    } catch (error) {
      const errorMessage = error.message || 'Error saving your choice'
      throw new Error(errorMessage)
    }
  }

  // Creates a student profile under the signed-in parent.
  //
  // studentViewOnly (Phase 2) creates a credential-light profile: first name
  // only, no Firebase Auth user, usable only from the parent's account. The
  // server rejects the call outright if credentials are sent alongside it, so
  // none are forwarded here.
  async function createStudentAccount({
    firstName,
    lastName = '',
    username = null,
    password = null,
    email = null,
    studentViewOnly = false
  }) {
    try {
      const createStudent = httpsCallable(functions, 'createStudentAccount')
      const result = await createStudent(
        studentViewOnly
          ? { firstName, lastName, studentViewOnly: true }
          : {
            firstName,
            lastName,
            ...(username ? { username } : {}),
            ...(password ? { password } : {}),
            ...(email ? { email } : {})
          }
      )

      return result.data
    } catch (error) {
      // Extract the error message from the Firebase Functions error
      const errorMessage = error.message || 'Error creating student account'
      throw new Error(errorMessage)
    }
  }

  // Gives an existing credential-light student a sign-in. The student's
  // document id becomes their Auth uid server-side, so all work already saved
  // under the profile stays attached to it.
  async function linkStudentCredentials({ studentId, username, password, email }) {
    try {
      const linkCredentials = httpsCallable(functions, 'linkStudentCredentials')
      const result = await linkCredentials({
        studentId,
        password,
        ...(username ? { username } : {}),
        ...(email ? { email } : {})
      })

      return result.data
    } catch (error) {
      const errorMessage = error.message || 'Error adding student login'
      throw new Error(errorMessage)
    }
  }

  // ── Student View + parent PIN (Phase 4) ────────────────────────────────────
  // Every one of these is a thin pass-through: entering Student View, leaving
  // it, and each PIN operation are all decided on the server. The client holds
  // no PIN, no hash, and no "I am allowed to act as this child" flag.

  // Opens or extends the session that authorizes work as one child. Called
  // again while Student View stays open so a long sitting cannot expire.
  async function startStudentViewSession(studentId) {
    try {
      const start = httpsCallable(functions, 'startStudentViewSession')
      const result = await start({ studentId })
      return result.data
    } catch (error) {
      throw callableError(error, 'Could not open Student View')
    }
  }

  // Leaving Student View. The PIN (when one is set) is verified server-side
  // before the session is deleted, so a wrong PIN keeps the child locked in.
  async function endStudentViewSession(pin = null) {
    try {
      const end = httpsCallable(functions, 'endStudentViewSession')
      const result = await end(pin ? { pin } : {})
      return result.data
    } catch (error) {
      throw callableError(error, 'Could not leave Student View')
    }
  }

  async function setParentPin(pin) {
    try {
      const setPin = httpsCallable(functions, 'setParentPin')
      const result = await setPin({ pin })
      return result.data
    } catch (error) {
      throw callableError(error, 'Could not save your PIN')
    }
  }

  async function changeParentPin(currentPin, newPin) {
    try {
      const changePin = httpsCallable(functions, 'changeParentPin')
      const result = await changePin({ currentPin, newPin })
      return result.data
    } catch (error) {
      throw callableError(error, 'Could not change your PIN')
    }
  }

  async function removeParentPin(pin) {
    try {
      const removePin = httpsCallable(functions, 'removeParentPin')
      const result = await removePin({ pin })
      return result.data
    } catch (error) {
      throw callableError(error, 'Could not remove your PIN')
    }
  }

  // Forgot PIN, step 1: a code goes to the parent's Auth email, so the reset
  // works from any device rather than only the locked one.
  async function requestParentPinReset() {
    try {
      const requestReset = httpsCallable(functions, 'requestParentPinReset')
      const result = await requestReset({})
      return result.data
    } catch (error) {
      throw callableError(error, 'Could not send the reset email')
    }
  }

  // Forgot PIN, step 2. A null/empty newPin clears the PIN instead of
  // replacing it, which is how a parent turns the lock off entirely.
  async function confirmParentPinReset(code, newPin = null) {
    try {
      const confirmReset = httpsCallable(functions, 'confirmParentPinReset')
      const result = await confirmReset({ code, ...(newPin ? { newPin } : {}) })
      return result.data
    } catch (error) {
      throw callableError(error, 'Could not reset your PIN')
    }
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
  }

  function logout() {
    return signOut(auth)
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email)
  }

  async function sendStudentPasswordReset(studentId) {
    try {
      const resetFn = httpsCallable(functions, 'sendStudentPasswordReset')
      const result = await resetFn({ studentId })
      return result.data
    } catch (error) {
      const errorMessage = error.message || 'Error sending password reset email'
      throw new Error(errorMessage)
    }
  }

  const loadUserProfile = useCallback(async (uid, options = {}) => {
    const generation = ++profileLoadGen.current
    const revisionAtStart = profileRevision.current
    try {
      const profileDoc = await readUserDocument(uid, options.fromServer === true)
      if (generation !== profileLoadGen.current) {
        return profileDoc.exists() ? profileDoc.data() : null
      }
      // A name or username save already updated this profile. Applying the
      // snapshot from a read that started earlier would reopen that modal.
      if (profileRevision.current !== revisionAtStart) {
        return profileDoc.exists() ? profileDoc.data() : null
      }
      const incoming = profileDoc.exists() ? profileDoc.data() : null
      const profile = keepClosedModal(userProfileRef.current, incoming)
      setUserProfile(profile)
      setLoadedProfileUid(profile ? uid : null)
      return profile
    } catch (error) {
      if (generation !== profileLoadGen.current) return null
      if (profileRevision.current !== revisionAtStart) return null
      console.error('Error fetching user profile:', error)
      setUserProfile(null)
      setLoadedProfileUid(null)
      return null
    }
  }, [])

  // Re-reads the signed-in user's profile from the server. Signup and the
  // blocking modals depend on this seeing the write that just finished,
  // not a cached miss from the moment before that write existed.
  const refreshUserProfile = useCallback(async () => {
    if (!auth.currentUser) return null
    return loadUserProfile(auth.currentUser.uid, { fromServer: true })
  }, [loadUserProfile])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user)
        await loadUserProfile(user.uid)
      } else {
        profileLoadGen.current += 1
        setCurrentUser(null)
        setUserProfile(null)
        setLoadedProfileUid(null)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [loadUserProfile])

  // lastActiveAt is a presence stamp, not a login stamp: any authenticated
  // page counts, including a persisted session. Writes are throttled and
  // skipped while the tab is hidden. Student View also stamps the child's
  // profile so view-only kids get a last-seen time without an Auth user.
  // A missing field stays absent until the next visit — existing accounts
  // are not backfilled.
  useEffect(() => {
    if (!canStampLastActiveAt({
      signedInUid: currentUser?.uid,
      loadedProfileUid
    })) {
      return undefined
    }

    let cancelled = false
    const inFlight = new Set()
    const storage = typeof window !== 'undefined' ? window.localStorage : null

    const writeIfDue = async (uid) => {
      if (!uid || cancelled || inFlight.has(uid)) return
      const now = Date.now()
      if (!shouldWriteLastActiveAt(readLastActiveWriteMs(uid, storage), now)) {
        return
      }

      inFlight.add(uid)
      markLastActiveWriteMs(uid, now, storage)
      try {
        await updateDoc(doc(db, 'users', uid), {
          lastActiveAt: serverTimestamp()
        })
      } catch (error) {
        if (!error || error.code !== 'permission-denied') {
          clearLastActiveWriteMs(uid, storage)
        }
        console.error('Failed to record lastActiveAt:', error)
      } finally {
        inFlight.delete(uid)
      }
    }

    const pulse = () => {
      if (typeof document !== 'undefined' &&
        document.visibilityState === 'hidden') {
        return
      }
      lastActiveUids({
        signedInUid: currentUser.uid,
        studentViewStudentId: studentViewActingStudentId
      }).forEach((uid) => {
        writeIfDue(uid)
      })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') pulse()
    }

    pulse()
    const interval = setInterval(pulse, LAST_ACTIVE_THROTTLE_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [currentUser, loadedProfileUid, studentViewActingStudentId])

  const value = {
    currentUser,
    userProfile,
    refreshUserProfile,
    studentViewActive,
    setStudentViewActive,
    setStudentViewActingStudentId,
    parentTaskModalActive,
    setParentTaskModalActive,
    signup,
    signupStudent,
    completeStudentUsername,
    login,
    logout,
    resetPassword,
    createStudentAccount,
    linkStudentCredentials,
    sendStudentPasswordReset,
    completeParentName,
    completeOnboardingChoice,
    startStudentViewSession,
    endStudentViewSession,
    setParentPin,
    changeParentPin,
    removeParentPin,
    requestParentPinReset,
    confirmParentPinReset
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}